import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { NBXStorage } from '@nbx/frontend-helpers/storage';
import ButtonBase from 'components/shared/ButtonBase';
import Spinner from 'components/shared/Spinner';
import ERRORS from 'constants/errors';
import { openIDLoginSignup, stopLoading, setError, reauthenticate } from 'reduxConfig/auth.creators';
import { OPEN_ID_PROVIDERS, OPEN_ID_LOGIN_HANDLERS } from 'constants/openIdProviders';
import { createLogger } from 'helpers/logger';
import { retryWhileNotTrue } from 'helpers/retry';
import styles from './SocialLogin.module.scss';
import { Capacitor } from '@capacitor/core';

const OPENID_LOGIN_IN_PROGRESS = 'auth:openIDLoginInProgress';

const log = createLogger('[social login][form]');

export const SocialLogin = ({
  dispatch,
  loading,
  disabled,
  setLoginMethod,
  loginMethod,
  styles: customStyles,
  referralCode,
  cardPartnershipCode,
  isLogin,
  providers,
  isReauthenticate
}) => {
  const { t } = useTranslation();
  const isWeb = window.PLATFORM === 'web';

  const handleSocialLogin = provider => {
    setLoginMethod(provider);
    if (window.FIREBASE.isRedirectFlow()) NBXStorage.setItem(OPENID_LOGIN_IN_PROGRESS, provider);
    dispatch(
      isReauthenticate
        ? reauthenticate({ provider })
        : openIDLoginSignup(
            OPEN_ID_LOGIN_HANDLERS[provider],
            referralCode || null,
            cardPartnershipCode || null
          )
    );
  };

  // handle redirect back from redirect flow
  useEffect(() => {
    let unsubscribe;
    const subscribeToCallbackFromIdp = async () => {
      unsubscribe = await window.FIREBASE.onAuthStateChanged(async (user, error) => {
        if (error) {
          log.error(`subscribeToCallbackFromIdp onAuthStateChanged error: ${error?.message}`, error);
          dispatch(setError(ERRORS.loginError));
        } else if (await NBXStorage.getItem(OPENID_LOGIN_IN_PROGRESS)) {
          try {
            const getRedirectResultWhileNotTrue = retryWhileNotTrue(
              window.FIREBASE.getRedirectResult,
              2,
              1000
            );
            const userCredential = await getRedirectResultWhileNotTrue();
            NBXStorage.removeItem(OPENID_LOGIN_IN_PROGRESS);
            if (userCredential) {
              dispatch(
                openIDLoginSignup(() => userCredential, referralCode || null, cardPartnershipCode || null)
              );
            } else {
              log.error(`FIREBASE.getRedirectResult userCredential error: ${error?.message}`, error);
              dispatch(setError(ERRORS.loginError));
              dispatch(stopLoading());
            }
          } catch (e) {
            NBXStorage.removeItem(OPENID_LOGIN_IN_PROGRESS);
            log.error(`FIREBASE.getRedirectResult error: ${e?.message}`, JSON.stringify(e));
            dispatch(setError(ERRORS.loginError));
            dispatch(stopLoading());
          }
        }
      });
    };

    if (isWeb && window.FIREBASE.isRedirectFlow()) subscribeToCallbackFromIdp();

    return unsubscribe;
  }, [dispatch, isWeb, referralCode, cardPartnershipCode]);

  const loadingVipps = loading && loginMethod === OPEN_ID_PROVIDERS.vipps;
  const loadingGoogle = loading && loginMethod === OPEN_ID_PROVIDERS.google;
  const loadingApple = loading && loginMethod === OPEN_ID_PROVIDERS.apple;

  return (
    <div className={classNames([styles.socialLogin, ...customStyles])}>
      {(!providers || providers.includes(OPEN_ID_PROVIDERS.google)) && (
        <ButtonBase
          noTarget
          onClick={() => handleSocialLogin(OPEN_ID_PROVIDERS.google)}
          styles={[
            styles.googleButton,
            { [styles.disabled]: disabled || loadingGoogle, [styles.loading]: loadingGoogle }
          ]}
          disabled={disabled || loading}
          fullWidth>
          {loadingGoogle && <Spinner />}
          <img src="/assets/icons/google.svg" alt="" />
          <span className={styles.text}>
            {isLogin ? t('common_log_in') : t('common_sign_up')} {t('common_with')} Google
          </span>
        </ButtonBase>
      )}

      {(!providers || providers.includes(OPEN_ID_PROVIDERS.apple)) && (
        <ButtonBase
          noTarget
          onClick={() => handleSocialLogin(OPEN_ID_PROVIDERS.apple)}
          styles={[
            styles.appleButton,
            { [styles.disabled]: disabled || loadingApple, [styles.loading]: loadingApple }
          ]}
          disabled={disabled || loading}
          fullWidth>
          {loadingApple && <Spinner />}
          <img src="/assets/icons/apple.png" alt="" />
          <span className={styles.text}>
            {isLogin ? t('common_log_in') : t('common_sign_up')} {t('common_with')} Apple
          </span>
        </ButtonBase>
      )}

      {!cardPartnershipCode &&
        Capacitor.getPlatform() !== 'ios' &&
        (!providers || providers.includes(OPEN_ID_PROVIDERS.vipps)) && (
          <ButtonBase
            noTarget
            onClick={() => handleSocialLogin(OPEN_ID_PROVIDERS.vipps)}
            styles={[
              styles.buttonVipps,
              { [styles.disabled]: disabled || loadingVipps, [styles.loading]: loadingVipps }
            ]}
            disabled={disabled || loading}
            fullWidth>
            {loadingVipps && <Spinner />}
            <span className={styles.text}>
              {isLogin ? t('common_log_in') : t('common_sign_up')} {t('common_with')}
            </span>
            <img src="/assets/icons/vipps.svg" alt="Vipps" />
          </ButtonBase>
        )}
    </div>
  );
};

SocialLogin.propTypes = {
  loading: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  loginMethod: PropTypes.string,
  setLoginMethod: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  styles: PropTypes.array,
  referralCode: PropTypes.string,
  cardPartnershipCode: PropTypes.string,
  isLogin: PropTypes.bool,
  providers: PropTypes.array
};

SocialLogin.defaultProps = {
  disabled: false,
  loginMethod: null,
  styles: [],
  referralCode: null,
  cardPartnershipCode: null,
  isLogin: true,
  providers: undefined
};
